import React from 'react';

import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';

import type { EmotionCache } from '@emotion/cache';
import type { Theme } from '@mui/material/styles';

export type ThemeManagerProperties = {
  /**
  Готовая тема. Собирает её приложение: наборы токенов у всех разные.
  */
  theme: Theme;
  /**
   * Кеш emotion. Нужен там, где важен порядок вставки стилей — у deals
   * `prepend: true`, чтобы стили MUI шли раньше пользовательских. Отсутствие
   * кеша — не «забыли», а «этому приложению порядок не важен».
   */
  emotionCache?: EmotionCache;
  children?: React.ReactNode;
};

/**
 * Провайдеры темы: один на все приложения (AF-34).
 *
 * До этого компонент существовал тремя разными реализациями одной задачи —
 * 315 строк у deals, 21 у applications, 546 у analyst, дифф deals ↔ analyst
 * 567 строк. Из них собственно компонентом было вот это: `ThemeProvider`,
 * `CssBaseline` и дети. Всё остальное — конфигурация темы, которая у каждого
 * приложения своя и потому осталась в приложении.
 *
 * Что здесь ценно помимо экономии строк: **`CssBaseline` идёт в паре с
 * `ThemeProvider` по одному решению, а не по трём.** Забыть его в одном из
 * трёх мест было можно; забыть его здесь — нельзя.
 *
 * Тема остаётся параметром, а не собирается внутри: наборы токенов у
 * приложений разные, и попытка собрать тему тут вернула бы сюда все три
 * конфигурации, только под флагами.
 */
export const ThemeManager: React.FC<ThemeManagerProperties> = ({
  theme,
  emotionCache,
  children,
}) => {
  const themed = (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );

  if (!emotionCache) {
    return themed;
  }

  return <CacheProvider value={emotionCache}>{themed}</CacheProvider>;
};
